import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ViewMode = 'grid' | 'list' | 'folder';
export type Theme = 'light' | 'dark' | 'system';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  /** Auto-dismiss after this many ms (0 = no auto-dismiss) */
  duration: number;
}

export interface UiState {
  /**
   * The user's preferred view mode for the media library.
   * Persisted to localStorage via redux-persist (Requirement 19.2).
   */
  viewMode: ViewMode;
  /** Whether the sidebar / navigation drawer is open on mobile */
  sidebarOpen: boolean;
  /** Active toast notifications */
  toasts: Toast[];
  /** Whether a global loading overlay is shown */
  globalLoading: boolean;
  /** Whether the rate-limit banner is visible */
  rateLimitBannerVisible: boolean;
  /** ISO timestamp when the rate limit resets */
  rateLimitRetryAfter: string | null;
}

const initialState: UiState = {
  viewMode: 'grid',
  sidebarOpen: false,
  toasts: [],
  globalLoading: false,
  rateLimitBannerVisible: false,
  rateLimitRetryAfter: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setViewMode(state, action: PayloadAction<ViewMode>) {
      state.viewMode = action.payload;
    },
    setSidebarOpen(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload;
    },
    addToast(state, action: PayloadAction<Omit<Toast, 'id'>>) {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      state.toasts.push({ id, ...action.payload });
    },
    removeToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
    clearToasts(state) {
      state.toasts = [];
    },
    setGlobalLoading(state, action: PayloadAction<boolean>) {
      state.globalLoading = action.payload;
    },
    showRateLimitBanner(state, action: PayloadAction<string | null>) {
      state.rateLimitBannerVisible = true;
      state.rateLimitRetryAfter = action.payload;
    },
    hideRateLimitBanner(state) {
      state.rateLimitBannerVisible = false;
      state.rateLimitRetryAfter = null;
    },
  },
});

export const {
  setViewMode,
  setSidebarOpen,
  addToast,
  removeToast,
  clearToasts,
  setGlobalLoading,
  showRateLimitBanner,
  hideRateLimitBanner,
} = uiSlice.actions;

// Selectors
export const selectViewMode = (state: { ui: UiState }) => state.ui.viewMode;
export const selectSidebarOpen = (state: { ui: UiState }) => state.ui.sidebarOpen;
export const selectToasts = (state: { ui: UiState }) => state.ui.toasts;
export const selectGlobalLoading = (state: { ui: UiState }) => state.ui.globalLoading;
export const selectRateLimitBannerVisible = (state: { ui: UiState }) =>
  state.ui.rateLimitBannerVisible;
export const selectRateLimitRetryAfter = (state: { ui: UiState }) => state.ui.rateLimitRetryAfter;

export default uiSlice.reducer;
